import React, { useEffect } from 'react';
import useAxiosPrivate from '../../../hooks/useAxiosPrivate';
import Users from '../../Users';
import Archived from '../../Archived';
import DateRange from '../../DateRange';
import { Link, useNavigate } from 'react-router';
import { Button, Card, CardBody, Col, Container, Form, FormGroup, Spinner } from 'reactstrap';
import useFilter from '../../../hooks/useFilter';
import useNavBar from '../../../hooks/useNavBar';
import LearningPlans from '../../LearningPlans';
import CourseStatuses from '../../CourseStatuses';
import useAuth from '../../../hooks/useAuth';

export default function LearningPlanReportForm({ myReports = false }) {
    const auth = useAuth();
    const axiosPrivate = useAxiosPrivate();
    const navigate = useNavigate();
    const users = !myReports ? Users() : null;
    const learningPlans = LearningPlans();
    const courseStatuses = CourseStatuses();
    const archived = Archived();
    const archivedDateRange = DateRange({
        title: '',
        initialHelpText: 'Active training plans are always included. Archived plans are included only if archived within the selected date range',
        startFieldName: 'archivedFromDate',
        endFieldName: 'archivedToDate',
        filterStartKey: 'archivedFromDate',
        filterEndKey: 'archivedToDate'
    });

    const { filter, setFilter } = useFilter();
    const { loading, setLoading } = useNavBar();
    

    const headers = 
    [
        "Full Name",
        "Training Plan Name",
        "Completion Percentage",
        "Enrollment Status",
        "Hours Required",
        "Hours Completed",
        "Hours Remaining",
        "Course",
        "Session Name",
        "Category",
        "Training Code",
        "Course Type",
        "Enrollment Date",
        "Valid From",
        "Valid To",
        "Course/Session Completion Date"
    ];    

    useEffect(() => {
        if ((myReports || !users.loading) && !learningPlans.loading && !archived.loading && !archivedDateRange.loading){
            setLoading(false);
        }

        const adjustedHeaders = [
            ...headers,
            ...(!myReports && users.includeInactiveUsers ? ["User Status"] : []), // User Status should only be included when not myReport and inactive users are included
            ...(archived.includeArchivedCompletions ? ["Active/Archived", "Archived Date", "Archived By"] : [])
        ];

        setFilter({
            users: myReports ? [auth.auth.user] : users.selectedUsers.map(user => user.value), //only logged in user should be selected for myReport version
            groups: myReports ? [] : users.selectedGroups.map(group => group.value),
            learningPlans: learningPlans.selected.map(learningPlan => learningPlan.value),
            courseStatuses: courseStatuses.selectedStatuses.map(courseStatus => courseStatus.value),
            headers: adjustedHeaders,
            includeArchivedCompletions: archived.includeArchivedCompletions,
            hasArchivedCompletions: archived.hasArchivedCompletions,
            archivedFromDate: archived.includeArchivedCompletions ? archivedDateRange.startDate : null,
            archivedToDate: archived.includeArchivedCompletions ? archivedDateRange.endDate : null,

            //users fields are not rendered for myReports
            ...(myReports ? {} : {
                selectAllUsers: users.selectAll,
                formUsers: users.users,
                formGroups: users.groups,
                includeInactiveUsers: users.includeInactiveUsers,
            }),
            formLearningPlans: learningPlans.learningPlans,
            formCourseStatuses: courseStatuses.courseStatuses,
            formSelectAllCourseStatuses: courseStatuses.selectAll
        });
    }, [...(myReports ? [] : [users.loading, users.selectAll, users.selectedUsers, users.users, users.selectedGroups, users.groups, users.includeInactiveUsers]),
        learningPlans.loading,
        learningPlans.selected, 
        learningPlans.learningPlans,
        courseStatuses.loading,
        courseStatuses.selectAll,
        courseStatuses.selectedStatuses,
        courseStatuses.courseStatuses,
        archived.loading,
        archived.includeArchivedCompletions,
        archived.hasArchivedCompletions,
        archivedDateRange.loading,
        archivedDateRange.startDate,
        archivedDateRange.endDate
    ]
    );

    const handleSubmit = async (e) => {
        e.preventDefault();

        const sendfilter = (
            (
                { users, groups, learningPlans, courseStatuses, headers, includeArchivedCompletions, hasArchivedCompletions, archivedFromDate, archivedToDate }) =>
                ({ users, groups, learningPlans, courseStatuses, headers, includeArchivedCompletions, hasArchivedCompletions, archivedFromDate, archivedToDate })
        )(filter);

        try{
            await axiosPrivate.post(myReports ? 'api/myreports/mylearningplan' : 'api/reports/learningplan', sendfilter);
            navigate(myReports ? '/myreports/reports' : '/reports');
        }
        catch(err){
            console.log(err);
        }
    }

    return (
        loading ? <Spinner variant="primary" animation="border" role="status" className='custom-spinner'/> :
        <Container>
            <Col>
                <FormGroup>
                    <Card>
                        <CardBody>                 
                            <FormGroup>
                                {learningPlans.render}
                            </FormGroup>
                        </CardBody>
                    </Card>
                </FormGroup>
                    {!myReports && ( //users fields are not rendered for myReports
                <FormGroup>                            
                    <Card>
                        <CardBody>
                            <FormGroup>
                                {users.render}
                            </FormGroup>
                        </CardBody>
                    </Card>  
                </FormGroup>
                )}
                    <FormGroup>
                        <Card>
                            <CardBody>
                                <FormGroup>
                                    {courseStatuses.render}
                                </FormGroup>
                            </CardBody>
                        </Card>
                    </FormGroup>
                    {
                        archived.hasArchivedCompletions ?
                            <FormGroup>
                                <Card>
                                    <CardBody>
                                        <FormGroup>
                                            {archived.render}
                                            {
                                                archived.includeArchivedCompletions ?
                                                    <div className='mt-3'>
                                                        {archivedDateRange.render}
                                                    </div>
                                                    : null
                                            }
                                        </FormGroup>
                                    </CardBody>
                                </Card>
                            </FormGroup>
                            : null
                    }
            </Col>
            <footer>
                <Form onSubmit={handleSubmit}>
                    <Button color="primary">Generate Report</Button>
                    <Link to={myReports ? '/myreports/' : '/'}> 
                        <Button className='btn-danger'>Back</Button>
                    </Link>
                </Form>
            </footer>
        </Container>
    );
}